import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, TriangleAlert, Share2, Bookmark, Star, ShoppingBag } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const ProductCard = ({ product, isWishlisted, wishlistItemId, onAddWishlist, onRemoveWishlist }) => {
  const navigate = useNavigate();
  const { addToCart, removeFromCart, getItemQuantity } = useCart();
  const quantity = getItemQuantity(product.id);
  const isOutOfStock = product.stock_quantity === 0 || !product.available;
  const [isWishLoading, setIsWishLoading] = useState(false);

  // Use passed-in ratings or default to 0
  const avgRating = product.average_rating || 0;
  const ratingsCount = product.ratings_count || 0;

  const slugify = (text) =>
    text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const handleProductClick = (e) => {
    // Don't navigate if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('.interactive-area')) {
      return;
    }
    const slug = slugify(product.name || 'product');
    navigate(`/products/${product.id}-${slug}`);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const slug = slugify(product.name || 'product');
    const url = `${window.location.origin}/share/products/${product.id}-${slug}`;
    const title = product.name;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        // Could trigger a toast here, but keeping it simple for now
        alert('Link copied!');
      }
    } catch { }
  };

  const toggleWishlist = async (e) => {
    e.stopPropagation();
    setIsWishLoading(true);
    if (isWishlisted && wishlistItemId) {
      await onRemoveWishlist();
    } else {
      await onAddWishlist();
    }
    setIsWishLoading(false);
  };

  const renderStars = (value) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={12}
        className={`${i < Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} sm:w-3 sm:h-3`}
      />
    ));
  };

  return (
    <div
      onClick={handleProductClick}
      className={`group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full border border-gray-100 overflow-hidden cursor-pointer ${isOutOfStock ? 'opacity-80 grayscale-[0.5]' : ''
        }`}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
        <img
          src={product.image_url || 'https://via.placeholder.com/400x300?text=Vihaan+Herbals'}
          alt={product.name}
          className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
          }}
        />

        {/* Overlay Badges */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-2">
          {isOutOfStock && (
            <span className="bg-red-500/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide flex items-center shadow-sm">
              <TriangleAlert size={12} className="mr-1" />
              Out of Stock
            </span>
          )}
          {!isOutOfStock && product.stock_quantity < 5 && (
            <span className="bg-orange-500/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide shadow-sm">
              Low Stock
            </span>
          )}
        </div>

        {/* Action Buttons (Top Right) */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-2 opacity-100 sm:opacity-0 sm:translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
          <button
            onClick={toggleWishlist}
            disabled={isWishLoading}
            className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-md backdrop-blur-md transition-all duration-200 ${isWishlisted
                ? 'bg-pink-50 text-pink-500 hover:bg-pink-100'
                : 'bg-white/90 text-gray-600 hover:bg-white hover:text-pink-500'
              }`}
            title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Bookmark className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isWishlisted ? 'fill-current' : ''}`} />
          </button>

          <button
            onClick={handleShare}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/90 backdrop-blur-md text-gray-600 hover:bg-white hover:text-green-600 flex items-center justify-center shadow-md transition-all duration-200"
            title="Share"
          >
            <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 flex flex-col flex-grow">
        <div className="flex-grow">
          <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
            <div className="flex">{renderStars(avgRating)}</div>
            <span className="text-[10px] sm:text-xs text-gray-500 ml-1">({ratingsCount})</span>
          </div>

          <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1 leading-tight group-hover:text-green-700 transition-colors line-clamp-2">
            {product.name}
          </h3>
        </div>

        {/* Price & Action Area */}
        <div className="pt-2 sm:pt-3 border-t border-gray-100 mt-2">
          <div className="flex items-center justify-between mb-2.5 sm:mb-3">
            <div className="flex flex-col">
              <span className="text-lg sm:text-2xl font-bold text-gray-900">{product.formatted_price}</span>
            </div>
            {quantity > 0 && (
              <div className="text-[10px] sm:text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 sm:py-1 rounded-full">
                In Cart: {quantity}
              </div>
            )}
          </div>

          {/* Cart Actions */}
          <div className="interactive-area">
            {!isOutOfStock ? (
              quantity > 0 ? (
                <div className="flex items-center justify-between bg-gray-50 rounded-full p-1 border border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromCart(product.id);
                    }}
                    className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-white text-red-500 shadow-sm hover:bg-red-50 transition-colors"
                  >
                    <Minus size={14} className="sm:w-4 sm:h-4" />
                  </button>
                  <span className="font-bold text-gray-900 w-6 sm:w-8 text-center text-sm sm:text-base">{quantity}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(product);
                    }}
                    disabled={quantity >= product.stock_quantity}
                    className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full shadow-sm transition-colors ${quantity >= product.stock_quantity
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-green-600 hover:bg-green-50'
                      }`}
                  >
                    <Plus size={14} className="sm:w-4 sm:h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addToCart(product);
                  }}
                  className="w-full py-2.5 px-3 sm:py-3 sm:px-4 bg-gray-900 hover:bg-green-600 text-white rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md transform active:scale-95 text-xs sm:text-sm"
                >
                  <ShoppingBag size={16} />
                  <span>Add to Cart</span>
                </button>
              )
            ) : (
              <button
                disabled
                className="w-full py-2.5 px-3 sm:py-3 sm:px-4 bg-gray-100 text-gray-400 rounded-full font-medium cursor-not-allowed flex items-center justify-center gap-2 border border-gray-200 text-xs sm:text-sm"
              >
                <span>Unavailable</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
